import z from 'zod';
import isNumeric from 'validator/lib/isNumeric';

// custom validator for numeric strings (empty string is valid)
const numericString = z
    .string()
    .trim()
    .refine((val) => val === '' || isNumeric(val), {
      message: 'Must be a number',
    })
    .default('');

export const FormObject = z.object({ 
  value: z.union([numericString, z.string(), z.boolean()]),
  valid: z.boolean().default(true),
  visible: z.boolean().default(true),
  history: z.array(z.any()).default([]),
});

// The complete policy schema
export const RawPolicySchema = z.object({
  peFlag: FormObject.extend({
    value: z.string().trim().default('')
  }),
  leadSyndicateId: FormObject.extend({
    value: z.string().trim().default(''),
  }),
  coverageTypeId: FormObject.extend({
    value: numericString,
  }),
  brokerName: FormObject.extend({
    value: z.string().trim().default(''),
  }),
  carrierId: FormObject.extend({
    value: z.string().trim().default(''),
  }),
  carrierName: FormObject.extend({
    value: z.string().trim().default(''),
  }),
  runoffFlag: FormObject.extend({
    value: z.boolean().default(false),
  }),
  sideAValue: FormObject.extend({
    value: z.string().trim().default(''),
  }),
  quotaShareTotalAmount: FormObject.extend({
    value: numericString,
  }),
  policyPremiumAmount: FormObject.extend({
    value: numericString,
  }),
  attachmentPointAmount: FormObject.extend({
    value: numericString,
  }),
  attachmentApplicable: FormObject.extend({
    value: z.boolean().default(true),
  }),
  aggregateLimitAmount: FormObject.extend({
    value: numericString,
  }),
  aggregateLimitApplicable: FormObject.extend({ 
    value: z.boolean().default(true),
  }),
  occurrenceLimitAmount: FormObject.extend({ 
    value: numericString,
  }),
  occurrenceLimitApplicable: FormObject.extend({
    value: z.boolean().default(true),
  }),
  retentionAmount: FormObject.extend({
    value: numericString,
  }),
  retentionApplicable: FormObject.extend({
    value: z.boolean().default(true),
  }),
  customRetentionLabel: FormObject.extend({
    value: z.string().trim().default(''),
  }),
  customCarrierName: FormObject.extend({
    value: z.string().trim().default(''),
  }),
  issuingCompanyId: FormObject.extend({
    value: numericString,
  }),
});

// The policy schema for existing policies in the system.
// The carrier name and the premium amount are not editable.
export const PolicySchema = RawPolicySchema.omit({
  coverageTypeId: true,
  carrierId: true,
  policyPremiumAmount: true,
});

// The policy line schema are for policy lines.
// Only certain fields are relevant for policy lines.
export const PolicyLineSchema = RawPolicySchema.omit({
  peFlag: true,
  leadSyndicateId: true,
  coverageTypeId: true,
  brokerName: true,
  carrierId: true,
  runoffFlag: true,
  sideAValue: true,
});

export const defaultFormFields = {
  carrierId: {value: '', valid: true, visible: true, history: []},
  carrierName: {value: '', valid: true, visible: true, history: []},
  issuingCompanyId: {value: '', valid: true, visible: true, history: []},
  policyPremiumAmount: {value: '', valid: true, visible: true, history: []},
  peFlag: {value: '', valid: true, visible: true, history: []},
  attachmentPointAmount: {value: '', valid: true, visible: true, history: []},
  attachmentApplicable: {value: true, valid: true, visible: true, history: []},
  aggregateLimitAmount: {value: '', valid: true, visible: true, history: []},
  aggregateLimitApplicable: {value: true, valid: true, visible: true, history: []},
  occurrenceLimitAmount: {value: '', valid: true, visible: true, history: []},
  occurrenceLimitApplicable: {value: true, valid: true, visible: true, history: []},
  quotaShareTotalAmount: {value: '', valid: true, visible: true, history: []},
  retentionAmount: {value: '', valid: true, visible: true, history: []},
  retentionApplicable: {value: true, valid: true, visible: true, history: []},
  customRetentionLabel: {value: '', valid: true, visible: true, history: []},
  customCarrierName: {value: '', valid: true, visible: true, history: []},
  leadSyndicateId: {value: '', valid: true, visible: true, history: []},
  coverageTypeId: {value: '', valid: true, visible: true, history: []},
  sideAValue: {value: '', valid: true, visible: true, history: []},
  brokerName: {value: '', valid: true, visible: true, history: []},
  runoffFlag: {value: false, valid: true, visible: true, history: []},
} as RawPolicy;

export type RawPolicy = z.infer<typeof RawPolicySchema>;
export type Policy = z.infer<typeof PolicySchema>;
export type LinePolicy = z.infer<typeof PolicyLineSchema>;
export type FormObject = z.infer<typeof FormObject>;
